import { useState, useEffect } from "react";
import { paymentsAPI, notificationsAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Bell, BellOff, BadgeCheck, CalendarClock, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const AccountControls = () => {
    const { user, refreshUser } = useAuth();
    const [smsOn, setSmsOn] = useState(user?.sms_notifications_enabled ?? true);
    const [savingPref, setSavingPref] = useState(false);
    const [testingSms, setTestingSms] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [sub, setSub] = useState(null);

    useEffect(() => {
        setSmsOn(user?.sms_notifications_enabled ?? true);
    }, [user?.sms_notifications_enabled]);

    useEffect(() => {
        if (user?.plan === "monthly") {
            paymentsAPI.getSubscriptionStatus()
                .then(r => setSub(r.data))
                .catch(() => {});
        }
    }, [user?.plan]);

    const togglePref = async (next) => {
        setSavingPref(true);
        try {
            await notificationsAPI.setPreference(next);
            setSmsOn(next);
            toast.success(next ? "SMS alerts on" : "SMS alerts off");
            refreshUser?.();
        } catch (e) {
            toast.error("Could not update preference");
        } finally {
            setSavingPref(false);
        }
    };

    const sendTest = async () => {
        setTestingSms(true);
        try {
            const res = await notificationsAPI.testSMS();
            if (res.data?.sent) toast.success("Test SMS sent");
            else if (res.data?.reason === "twilio_not_configured")
                toast.message("SMS provider not configured yet — we logged the test instead.");
            else toast.error(`SMS failed: ${res.data?.reason || "unknown"}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test SMS failed");
        } finally {
            setTestingSms(false);
        }
    };

    const cancelSub = async () => {
        if (!window.confirm("Cancel subscription at the end of the billing period?")) return;
        setCanceling(true);
        try {
            await paymentsAPI.cancelSubscription();
            toast.success("Subscription will cancel at period end");
            const r = await paymentsAPI.getSubscriptionStatus();
            setSub(r.data);
            refreshUser?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cancel failed");
        } finally {
            setCanceling(false);
        }
    };

    return (
        <Card className="border-border" data-testid="account-controls">
            <CardHeader>
                <CardTitle className="text-lg font-serif">Account Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Plan Status */}
                {user?.has_paid && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-accent/10 border border-accent/30">
                        <BadgeCheck className="h-5 w-5 text-accent mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">
                                {user.plan === "monthly" ? "Monthly Plan" : "Full Access"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {user.plan === "monthly"
                                    ? sub?.cancel_at_period_end
                                        ? `Cancels on ${sub?.current_period_end?.slice(0, 10) || "period end"}`
                                        : "Renews monthly · cancel anytime"
                                    : "One-time payment · lifetime access"}
                            </p>
                        </div>
                    </div>
                )}

                {/* SMS Toggle */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-start gap-2">
                        {smsOn ? (
                            <Bell className="h-4 w-4 text-primary mt-0.5" />
                        ) : (
                            <BellOff className="h-4 w-4 text-muted-foreground mt-0.5" />
                        )}
                        <div>
                            <Label htmlFor="sms-toggle" className="text-sm font-medium">
                                SMS dispute alerts
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                Get a text whenever AI generates new next steps.
                            </p>
                        </div>
                    </div>
                    <Switch
                        id="sms-toggle"
                        checked={smsOn}
                        disabled={savingPref}
                        onCheckedChange={togglePref}
                        data-testid="sms-toggle"
                    />
                </div>

                {user?.phone && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full"
                        onClick={sendTest}
                        disabled={testingSms}
                        data-testid="send-test-sms-btn"
                    >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {testingSms ? "Sending..." : "Send test SMS"}
                    </Button>
                )}

                {/* Cancel Subscription */}
                {user?.plan === "monthly" && !sub?.cancel_at_period_end && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-destructive hover:text-destructive"
                        onClick={cancelSub}
                        disabled={canceling}
                        data-testid="cancel-subscription-btn"
                    >
                        <X className="h-4 w-4 mr-2" />
                        {canceling ? "Canceling..." : "Cancel subscription"}
                    </Button>
                )}

                {sub?.cancel_at_period_end && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-muted text-xs">
                        <CalendarClock className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <p className="text-muted-foreground">
                            Subscription ends {sub?.current_period_end?.slice(0, 10) || "soon"}.
                            You keep full access until then.
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default AccountControls;
